import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const IMAGES = [
  { src: "/assets/corsi/corso-1.jpg?v=3", alt: "Brindisi di gruppo alla fine di un corso, nell'enoteca", caption: "Il brindisi finale, insieme ai corsisti" },
  { src: "/assets/corsi/corso-2.jpg?v=3", alt: "Foto di gruppo dei corsisti al termine di una serata di degustazione", caption: "Foto di gruppo a fine serata" },
  { src: "/assets/corsi/corso-3.jpg?v=3", alt: "Stefano Cremonese conduce una lezione del corso al microfono", caption: "Stefano guida la lezione, calice alla mano" },
  { src: "/assets/corsi/corso-4.jpg?v=3", alt: "I corsisti seguono la degustazione guidata ai tavoli", caption: "Degustazione guidata ai tavoli" },
  { src: "/assets/corsi/corso-5.jpg?v=3", alt: "Concentrazione durante l'assaggio: calici e schede di degustazione", caption: "L'assaggio: calici e schede di degustazione" },
  { src: "/assets/corsi/corso-6.jpg?v=1", alt: "Foto di gruppo dei corsisti con gli attestati di partecipazione", caption: "Consegna degli attestati a fine corso" },
  { src: "/assets/corsi/corso-7.jpg?v=1", alt: "Corsista assapora un calice di vino rosso durante la degustazione", caption: "Il primo sorso, in degustazione" },
  { src: "/assets/corsi/corso-8.jpg?v=1", alt: "Aula del corso con i partecipanti che alzano la mano", caption: "Lezione interattiva, tra assaggi e domande" },
  { src: "/assets/corsi/corso-9.jpg?v=1", alt: "Corsisti concentrati mentre degustano al tavolo", caption: "Concentrazione all'assaggio guidato" },
  { src: "/assets/corsi/corso-10.jpg?v=1", alt: "Foto di gruppo dei corsisti a fine percorso", caption: "Un corso al completo, tra sorrisi e brindisi" },
  { src: "/assets/corsi/corso-11.jpg?v=1", alt: "Corsisti in visita in cantina davanti al muro della Lugana", caption: "In visita in cantina, tra i corsisti" },
];

export default function CourseGallery() {
  const [open, setOpen] = useState(null);

  const close = useCallback(() => setOpen(null), []);
  const prev = useCallback(() => setOpen((i) => (i - 1 + IMAGES.length) % IMAGES.length), []);
  const next = useCallback(() => setOpen((i) => (i + 1) % IMAGES.length), []);

  useEffect(() => {
    if (open === null) return;
    const lenis = window.__lenis;
    lenis?.stop();
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open, close, prev, next]);

  return (
    <div data-testid="course-gallery">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {IMAGES.map((img, i) => (
          <figure key={img.src} className="group">
            <button
              data-testid={`course-gallery-item-${i}`}
              onClick={() => setOpen(i)}
              className="relative block w-full overflow-hidden rounded-xl aspect-[4/3] border border-nero/10 focus:outline-none focus:ring-2 focus:ring-oro/60"
              aria-label={`Apri foto: ${img.alt}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <span className="absolute inset-0 bg-bordeaux/0 group-hover:bg-bordeaux/15 transition-colors duration-300" />
            </button>
            <figcaption data-testid={`course-gallery-caption-${i}`}
                        className="mt-2 text-xs sm:text-sm text-nero/60 leading-snug">
              {img.caption}
            </figcaption>
          </figure>
        ))}
      </div>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            data-testid="course-gallery-lightbox"
            className="fixed inset-0 z-[120] bg-nero/95 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          >
            <button
              data-testid="course-gallery-close"
              onClick={close}
              className="absolute top-5 right-5 h-11 w-11 rounded-full border border-panna/25 text-panna flex items-center justify-center hover:bg-panna/10 transition-colors"
              aria-label="Chiudi"
            >
              <X size={20} />
            </button>
            <button
              data-testid="course-gallery-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 sm:left-8 h-12 w-12 rounded-full border border-panna/25 text-panna flex items-center justify-center hover:bg-panna/10 transition-colors"
              aria-label="Precedente"
            >
              <ChevronLeft size={22} />
            </button>
            <motion.img
              key={open}
              src={IMAGES[open].src}
              alt={IMAGES[open].alt}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[90vw] rounded-xl shadow-2xl object-contain"
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.25 }}
            />
            <button
              data-testid="course-gallery-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 sm:right-8 h-12 w-12 rounded-full border border-panna/25 text-panna flex items-center justify-center hover:bg-panna/10 transition-colors"
              aria-label="Successiva"
            >
              <ChevronRight size={22} />
            </button>
            <span data-testid="course-gallery-counter"
                  className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center text-panna/70 text-xs uppercase tracking-[0.25em] px-4">
              <span className="block mb-1 normal-case tracking-normal text-sm text-panna/85">{IMAGES[open].caption}</span>
              {open + 1} / {IMAGES.length}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
